import React, { useEffect, useRef, useState } from "react";
import { ServerInterface } from "src/store/mine_craft/serversSlice";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { fetchServerPlans } from "src/actions/mine_craft/serverPlansActions";
import { fetchServerVersions } from "src/actions/mine_craft/serversActions";
import { selectServerPlanEntities, selectServerPlanIds } from "src/store/mine_craft/serverPlansSlice";

import { Button, Form, Input, InputNumber, Radio, Select, Switch } from "antd";
import FileDropzone from "src/componets/FileDropzone";
import PlansHelp from "./_PlansHelp";

// The picker is fed live from crafty-servers (GET /versions); this is
// what it falls back to when that call fails, so the form still submits.
// `latest` always comes first — Bedrock clients auto-update and cannot
// join an older server, so pinning a build only makes sense for an addon
// that breaks on newer ones.
const FALLBACK_STABLE_VERSIONS = ['1.21.90', '1.21.80', '1.21.70', '1.21.60', '1.21.50'];

interface BedrockVersions {
  latest?: string;
  stable: string[];
}

const CREATE_DEFAULTS = {
  hosted: true,
  hosted_config: {
    version:    'latest',
    gamemode:   'adventure',
    difficulty: 'normal',
    access:     'public',
    power_mode: 'afk'
  }
};

interface propsInterface {
  params?: ServerInterface | undefined;
  onFinish: Function;
  // Shown only on create: provisioning through crafty-servers happens
  // once — edits of a hosted server go through the dedicated hosted
  // controls on the Show page.
  allowHosted?: boolean;
}

const _Form = ({ params, onFinish, allowHosted }: propsInterface) => {
  const [form]   = Form.useForm();
  const dispatch = useAppDispatch();
  const hosted   = Form.useWatch('hosted', form);
  const planId   = Form.useWatch('server_plan_id', form);
  const access   = Form.useWatch(['hosted_config', 'access'], form);
  const powerMode = Form.useWatch(['hosted_config', 'power_mode'], form);

  const planIds = useAppSelector(selectServerPlanIds);
  const plans   = useAppSelector(selectServerPlanEntities);

  const [versions, setVersions] = useState<BedrockVersions>({ stable: FALLBACK_STABLE_VERSIONS });

  const versionOptions = [
    { value: 'latest', label: versions.latest ? `latest (${versions.latest})` : 'latest' },
    ...versions.stable
      .filter(version => version !== 'latest')
      .map(version => ({ value: version, label: version }))
  ];

  useEffect(() => {
    if (!allowHosted) return;

    dispatch(fetchServerVersions())
      .then(action => {
        if (fetchServerVersions.fulfilled.match(action) && Array.isArray(action.payload?.stable)) {
          setVersions({ latest: action.payload.latest, stable: action.payload.stable });
        }
      })
      .catch(console.error);
  }, [allowHosted, dispatch]);

  const planOptions = planIds
    .map(id => plans[id])
    .filter(Boolean)
    .map((plan: any) => ({
      value: plan.id,
      label: `${plan.title} — ${plan.memory_mb} MB · ${plan.max_players} slots · ${plan.coins} coins`
    }));

  const initialValues = allowHosted ? { ...CREATE_DEFAULTS, ...params } : params;

  // Applied once per opened form: re-running it would fight the admin
  // every time they clear the select to go Custom.
  const planDefaultApplied = useRef(false);

  useEffect(() => {
    form.resetFields();
    planDefaultApplied.current = false;
  }, [params, form]);

  useEffect(() => {
    if (allowHosted && params?.app_id) {
      dispatch(fetchServerPlans({ app_id: params.app_id, limit: 100 }));
    }
  }, [allowHosted, params?.app_id, dispatch]);

  // Plans arrive after the form mounts, so the default lands here rather
  // than in CREATE_DEFAULTS. Last in the list is the top tier — the
  // listing sorts by `order` descending.
  useEffect(() => {
    if (!allowHosted || planDefaultApplied.current || !planOptions.length) {
      return;
    }

    planDefaultApplied.current = true;
    form.setFieldValue('server_plan_id', planOptions[planOptions.length - 1].value);
  }, [allowHosted, planOptions, form]);

  return (
    <Form
      form={form}
      onFinish={values => onFinish(values)}
      layout="vertical"
      className='v-form'
      initialValues={initialValues}
    >
      <Form.Item name='app_id' noStyle>
        <Input type="hidden" />
      </Form.Item>

      {allowHosted &&
        <Form.Item
          label="Type"
          name='hosted'
          extra='Hosted provisions a real Bedrock server through crafty-servers — the listing stays unpublished and gets its IP/port automatically once the server is ready. External only lists a server running elsewhere.'
        >
          <Radio.Group optionType="button" buttonStyle='solid'>
            <Radio value={true}>Hosted</Radio>
            <Radio value={false}>External</Radio>
          </Radio.Group>
        </Form.Item>
      }

      {hosted &&
        <>
          <Form.Item
            name={['hosted_config', 'access']}
            label="Access"
            extra='Bedrock has no native password — use allowlist + gamertags to restrict access.'
          >
            <Radio.Group optionType="button" buttonStyle='solid'>
              <Radio value='public'>Public</Radio>
              <Radio value='allowlist'>Private</Radio>
            </Radio.Group>
          </Form.Item>

          {access === 'allowlist' &&
            <Form.Item name={['hosted_config', 'allowlist']} label="Allowlist (gamertags)">
              <Select mode='tags' tokenSeparators={[',']} placeholder='Add gamertags' />
            </Form.Item>
          }
        </>
      }

      {!hosted &&
        <Form.Item label="Published" name='published' valuePropName='checked'>
          <Switch />
        </Form.Item>
      }

      <h6 className='v-content-label o-sm'>LISTING</h6>

      <div className='v-group-dropzone'>
        <FileDropzone name='image' label='Image' preview={params?.image_url} />
      </div>

      <Form.Item name='title' label="Title">
        <Input placeholder='Title' />
      </Form.Item>

      <Form.Item name='description' label="Description">
        <Input.TextArea rows={4} />
      </Form.Item>

      <Form.Item name='description_html' label="Body HTML">
        <Input.TextArea rows={4} />
      </Form.Item>

      {!hosted &&
        <>
          <h6 className='v-content-label o-sm'>CONNECTION</h6>

          <Form.Item name='ip' label="IP / Host">
            <Input placeholder='play.example.com' />
          </Form.Item>

          <Form.Item name='port' label="Port">
            <InputNumber min={1} max={65535} placeholder='19132' style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name='version' label="Version">
            <Input placeholder='1.21.x' />
          </Form.Item>
        </>
      }

      {hosted &&
        <>
          <h6 className='v-content-label o-sm'>PLAN<PlansHelp /></h6>

          <Form.Item
            name='server_plan_id'
            label="Plan"
            extra='The plan dictates memory, storage, CPU and player slots.'
          >
            <Select
              allowClear
              placeholder='Custom (set the resources below)'
              options={planOptions}
            />
          </Form.Item>

          {!planId &&
            <>
              <Form.Item name={['hosted_config', 'limits', 'memory_mb']} label="Memory (MB)">
                <InputNumber min={256} max={8192} style={{ width: '100%' }} placeholder='2048' />
              </Form.Item>

              <Form.Item name={['hosted_config', 'limits', 'disk_mb']} label="Disk (MB)">
                <InputNumber min={512} max={20480} style={{ width: '100%' }} placeholder='4096' />
              </Form.Item>

              <Form.Item name={['hosted_config', 'limits', 'cpu_percent']} label="CPU (%)">
                <InputNumber min={25} max={400} style={{ width: '100%' }} placeholder='100' />
              </Form.Item>

              <Form.Item name={['hosted_config', 'max_players']} label="Player slots">
                <InputNumber min={1} max={200} style={{ width: '100%' }} />
              </Form.Item>
            </>
          }

          <h6 className='v-content-label o-sm'>WORLD</h6>

          <Form.Item
            name={['hosted_config', 'version']}
            label="Bedrock version"
            extra='Keep latest unless an addon breaks on newer builds — players on a newer client cannot join an older server.'
          >
            <Select showSearch options={versionOptions} />
          </Form.Item>

          <Form.Item name={['hosted_config', 'gamemode']} label="Gamemode">
            <Radio.Group optionType="button" buttonStyle='solid'>
              <Radio value='survival'>Survival</Radio>
              <Radio value='creative'>Creative</Radio>
              <Radio value='adventure'>Adventure</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item name={['hosted_config', 'difficulty']} label="Difficulty">
            <Radio.Group optionType="button" buttonStyle='solid'>
              <Radio value='peaceful'>Peaceful</Radio>
              <Radio value='easy'>Easy</Radio>
              <Radio value='normal'>Normal</Radio>
              <Radio value='hard'>Hard</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item name={['hosted_config', 'seed']} label="Seed">
            <Input placeholder='random when empty' />
          </Form.Item>

          <Form.Item name={['hosted_config', 'cheats']} label="Cheats" valuePropName='checked'>
            <Switch />
          </Form.Item>

          <h6 className='v-content-label o-sm'>POWER</h6>

          <Form.Item
            name={['hosted_config', 'power_mode']}
            label="Power mode"
            extra='Sleep when idle stops the server after a quiet spell and wakes it on the first join — it stays in the players list meanwhile. Always on never sleeps and restarts itself after a crash.'
          >
            <Radio.Group optionType="button" buttonStyle='solid'>
              <Radio value='afk'>Sleep when idle</Radio>
              <Radio value='always_on'>Always on</Radio>
            </Radio.Group>
          </Form.Item>

          {powerMode !== 'always_on' &&
            <Form.Item name={['hosted_config', 'auto_stop_minutes']} label="Sleep after (minutes without players)">
              <InputNumber min={0} max={1440} style={{ width: '100%' }} placeholder='provider default' />
            </Form.Item>
          }
        </>
      }

      <Form.Item>
        <Button className="v-btn v-btn-primary" type="primary" htmlType="submit">
          Submit
        </Button>
      </Form.Item>
    </Form>
  )
}

export default _Form;
